import Utils from "../../libs/Utils"
import createSpline, { type TDataPoint } from "./Spline"

export type TChartLabel = (val:number) => string
export type TXAxis = number|Date
export type TLineChartData = [TXAxis,number]

const DENOM :[number,string][] = [[1_000_000_000, "B"], [1_000_000, "M"], [1_000, "K"]]
const LABEL_Y = 32
const PADDING = 8
const DECIMAL = 2

function n2label(value:number) {
    for(const denom of DENOM) {
        const [d,label] = denom
        if (value>d) return (value/d).toFixed(DECIMAL) + label
    }
    return value.toFixed(DECIMAL)
}

function n2str(value:number){
    return value.toFixed(2)
}

export default class LineChartCanvas {
    private _canvas:HTMLCanvasElement
    private _rows:TLineChartData[]
    private _g:CanvasRenderingContext2D|null
    private _canvasWidth:number = 0
    private _canvasHeight:number = 0
    private _paddingLeft:number = 0
    private _paddingBottom:number = 0
    private _graphWidth:number = 0
    private _graphHeight:number = 0
    private _data:TDataPoint[] = []
    private _spine:(x:number) => number = () => 0
    private _idata:ImageData|undefined

    private _labelY:TChartLabel
    private _value:TChartLabel

    constructor(canvas:HTMLCanvasElement, rows:TLineChartData[], labelY:TChartLabel|undefined, value:TChartLabel|undefined){
        this._canvas = canvas
        this._rows = rows
        this._labelY = labelY || n2label
        this._value = value || n2str

        const g = this._g = canvas.getContext("2d")        
        if (!g) return 

        const rect = canvas.getBoundingClientRect()
        this._canvasWidth = rect.width * window.devicePixelRatio
        this._canvasHeight = rect.height * window.devicePixelRatio

        canvas.width = this._canvasWidth
        canvas.height = this._canvasHeight

        g.clearRect(0, 0, this._canvasWidth, this._canvasHeight)

        const minY = Math.min(...rows.map(d => d[1]))
        var maxY = Math.max(...rows.map(d => d[1]))
        const rangeY = (maxY - minY)
        maxY += rangeY*0.1

        const axisY : string[] = []
        var textWidth = 0

        g.font = "12px arial"
        const c_axisY = this._canvasHeight/LABEL_Y
        for(var i=0; i<c_axisY; i++) {
            const text = this._labelY((i+1)/c_axisY * rangeY + minY)
            axisY.push(text)
            const w = g.measureText(text).width
            if (w>textWidth) textWidth = w
        }

        this._paddingLeft = textWidth + PADDING
        this._paddingBottom = 1
        this._graphWidth = this._canvasWidth - this._paddingLeft - PADDING
        this._graphHeight = this._canvasHeight-this._paddingBottom

        for(var i=0; i<c_axisY; i++) {
            const y = this._graphHeight - (i+1)*LABEL_Y
            g.beginPath()
            g.moveTo(this._paddingLeft, y)
            g.lineTo(this._canvasWidth-1, y)
            g.strokeStyle ="#eee"
            g.lineWidth = 0.5
            g.stroke()

            g.textBaseline = "top"
            g.textAlign = "right"
            g.fillStyle = "#aaa";
            g.fillText(axisY[i], this._paddingLeft - PADDING, y)
            
        }

        g.beginPath()
        g.moveTo(this._paddingLeft-1, 0)
        g.lineTo(this._paddingLeft-1, this._canvasHeight-this._paddingBottom+1)
        g.lineTo(this._canvasWidth, this._canvasHeight-this._paddingBottom+1)
        g.strokeStyle = "#333"
        g.lineWidth = 2
        g.stroke()

        const scale = rangeY/maxY * this._graphHeight
        if (!scale) return

        const len = rows.length
        this._data = rows.map((point,i) => [(i/len)*this._graphWidth, (point[1]-minY)/rangeY*scale])
        this._spine = createSpline(this._data, 0.98)

        var i = 0;
        g.beginPath()
        while(i<=this._graphWidth) {
            const y = this._spine(i)
            if (i==0) g.moveTo(this._paddingLeft + i, this._graphHeight-y)
            else g.lineTo(this._paddingLeft + i, this._graphHeight-y)
            if (i==this._graphWidth) break;
            else i = Math.min(i+5, this._graphWidth)
        }

        g.strokeStyle = "#10B981";
        g.lineWidth = 2;
        g.stroke()
        this._idata = g.getImageData(0, 0, this._canvasWidth, this._canvasHeight)        
    }

    clear(){
        if (!this._g || !this._idata) return

        this._g.clearRect(0, 0, this._canvasWidth, this._canvasHeight)
        this._g.putImageData(this._idata, 0, 0)
    }

    hover(e:{clientX:number,clientY:number}) {
        if (!this._g) return

        this.clear()
        const g = this._g
        const rect = this._canvas.getBoundingClientRect()
        const clientX = (e.clientX-rect.left) * window.devicePixelRatio
        const offsetX = (clientX - this._paddingLeft)
        var x = 0, y = 0, label = "", value = 0
        for(var i=0; i<this._data.length; i++) {
            if (this._data[i][0]>=offsetX) {
                x = (i/this._rows.length)*this._graphWidth
                y = this._graphHeight - this._spine(x)
                label = this._rows[i][0].toString()
                value = this._rows[i][1]
                break;
            }
        }
        if (i==this._data.length) return

        const left = x + this._paddingLeft
        g.beginPath()
        g.ellipse(left, y, 4, 4, Math.PI*2, 0, Math.PI*2)
        g.lineWidth = 0.5
        g.fillStyle = "#888"
        g.fill()

        var top = y
        if (y<32) {
            g.textBaseline = "top"
            top += PADDING
        }
        else {
            g.textBaseline = "bottom"
            top -= PADDING
        }

        g.fillStyle = "#333";
        g.font = "12px arial";

        const text = `${label} - ${this._value(value)}`
        const textWidth = g.measureText(text).width

        if (left<textWidth) g.textAlign = "left"
        else if (left+textWidth>this._canvasWidth) g.textAlign = "right"
        else g.textAlign = "center"

        g.fillText(text, left, top)
    }
}